import { Fragment, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { MapContainer, Marker, Popup, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import { DivIcon, LatLngBounds, LatLngExpression, Map as LeafletMap } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import SectionHeader from '../../components/SectionHeader';
import Spinner from '../../components/Spinner';
import Button from '../../components/Button';
import { useGatesWMinRoute } from '../../hooks/useGates';
import markerIconPng from '../../icons/marker-icon-green.png';
import endMarkerIconPng from '../../icons/marker-icon-red.png';
import { Gate } from '../../types/Gate';
import { useLocation, useNavigate } from 'react-router-dom';

type RouteSelectionState = {
  returnTo?: string;
  selectedGateId?: string | null;
  sessionId?: string; // When coming from session view
  hasCallback?: boolean; // Indicates if a callback function is available
};

type GateSummary = {
  id: string;
  start: LatLngExpression;
  end: LatLngExpression | null;
  label: string;
  routeDescription: string;
  popupLabel: string;
  isValid: boolean;
  gate: Gate;
  order: number;
};

const FALLBACK_CENTER: LatLngExpression = [51.5074, -0.1278]; // Default to London if no gates returned

export default function RouteSelection(): ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const routeState = location.state as RouteSelectionState | null;
  const returnTo = routeState?.returnTo;
  const selectedGateIdFromState = routeState?.selectedGateId ?? null;
  const sessionId = routeState?.sessionId; // Session ID if coming from session view

  const { data: gates, isPending, isError } = useGatesWMinRoute();

  const createGateIcon = useCallback(
    (type: 'start' | 'end', label: string) =>
      new DivIcon({
        className: '',
        html: `
          <div style="position: relative; display: inline-flex; align-items: center; justify-content: center;">
            <img src="${type === 'start' ? markerIconPng : endMarkerIconPng}" alt="Gate marker" style="width: 25px; height: 41px; filter: drop-shadow(0 1px 2px rgba(0,0,0,0.25));" />
            <span style="position: absolute; top: -12px; left: 50%; transform:
             translateX(-50%); min-width: 20px; padding: 2px 6px; border-radius: 9999px; border: 2px solid #ffffff;
              background: ${
                type === 'start' ? '#0f766e' : '#b91c1c'
              }; color: #ffffff; font-size: 12px; font-weight: 700; line-height: 1; text-align: center;">${label}</span>
          </div>
        `,
        iconAnchor: [12, 41],
        popupAnchor: [0, -36],
        tooltipAnchor: [0, -36],
      }),
    [markerIconPng, endMarkerIconPng],
  );

  const gateSummaries = useMemo<GateSummary[]>(() => {
    if (!gates) return [];

    const summaries = gates
      .map((gate: Gate) => {
        const startLat = gate.gates_lat?.[0];
        const startLon = gate.gates_lon?.[0];
        if (typeof startLat !== 'number' || typeof startLon !== 'number') {
          return null;
        }

        const endLat = gate.gates_lat?.[1];
        const endLon = gate.gates_lon?.[1];
        const end =
          typeof endLat === 'number' && typeof endLon === 'number' ? ([endLat, endLon] as LatLngExpression) : null;

        const labelCandidate =
          (gate.address && gate.address.trim()) ||
          (gate.description && gate.description.trim()) ||
          (gate.notes && gate.notes.trim()) ||
          (gate.route_id && gate.route_id.trim());
        const label = labelCandidate && labelCandidate.length > 0 ? labelCandidate : gate.id;
        const routeDescription = gate.route_description?.trim() || '';
        const popupLabel = (gate.notes && gate.notes.trim()) || routeDescription || label;

        return {
          id: gate.id,
          start: [startLat, startLon] as LatLngExpression,
          end,
          label,
          routeDescription,
          popupLabel,
          isValid: gate.valid,
          gate,
        };
      })
      .filter(Boolean) as Omit<GateSummary, 'order'>[];

    return summaries.map((summary, index) => ({ ...summary, order: index + 1 }));
  }, [gates]);

  const [activeGateId, setActiveGateId] = useState<string | null>(selectedGateIdFromState);
  const [focusMode, setFocusMode] = useState<'all' | 'selected'>(selectedGateIdFromState ? 'selected' : 'all');

  useEffect(() => {
    if (!selectedGateIdFromState) return;
    setActiveGateId((prev) => (prev === selectedGateIdFromState ? prev : selectedGateIdFromState));
    setFocusMode('selected');
  }, [selectedGateIdFromState]);

  const selectedGate = useMemo(
    () => gateSummaries.find((gate) => gate.id === activeGateId) ?? null,
    [gateSummaries, activeGateId],
  );

  const allBounds = useMemo(() => {
    if (!gateSummaries.length) return null;
    const positions: LatLngExpression[] = [];
    gateSummaries.forEach((gate) => {
      positions.push(gate.start);
      if (gate.end) positions.push(gate.end);
    });
    return positions.length ? new LatLngBounds(positions) : null;
  }, [gateSummaries]);

  const selectedBounds = useMemo(() => {
    if (!selectedGate) return null;
    const points: LatLngExpression[] = [selectedGate.start];
    if (selectedGate.end) points.push(selectedGate.end);
    return new LatLngBounds(points);
  }, [selectedGate]);

  const bounds = focusMode === 'selected' && selectedBounds ? selectedBounds : allBounds;
  const mapCenter = selectedGate?.start || gateSummaries[0]?.start || FALLBACK_CENTER;

  const [showBothGates, setShowBothGates] = useState(false);
  const [fitBoundsTrigger, setFitBoundsTrigger] = useState(0);

  const handleMapBoundsChange = useCallback((shouldShow: boolean) => {
    setShowBothGates((prev) => (prev === shouldShow ? prev : shouldShow));
  }, []);

  const handleFocusGate = useCallback((gateId: string) => {
    setActiveGateId(gateId);
    setFocusMode('selected');
    setFitBoundsTrigger((prev) => prev + 1);
  }, []);

  const handleSelectGate = useCallback(
    (summary: GateSummary) => {
      handleFocusGate(summary.id);

      // Check if there's a callback function to call
      if (routeState?.hasCallback && (window as any).__gateSelectCallback) {
        // Call the callback directly
        (window as any).__gateSelectCallback(summary.gate.id);
        // Navigate back without state
        if (returnTo) {
          navigate(returnTo);
        }
      } else if (returnTo) {
        // Fallback to old behavior for backward compatibility
        navigate(returnTo, { state: { selectedGateId: summary.gate.id, fromSession: !!sessionId } });
      }
    },
    [handleFocusGate, navigate, returnTo, sessionId, routeState?.hasCallback],
  );

  const handleZoomAll = useCallback(() => {
    setFocusMode('all');
    setFitBoundsTrigger((prev) => prev + 1);
  }, []);

  const backLink = returnTo ? { to: returnTo, text: sessionId ? 'Back to Session' : 'Back to Calibration' } : undefined;

  return (
    <div className="cr-w-full">
      <SectionHeader title="Route Selection" isLoading={isPending} backLink={backLink} />

      <div className="cr-mt-6 cr-grid cr-gap-6">
        <div className="cr-relative cr-h-[500px] cr-overflow-hidden cr-rounded-xl cr-bg-white cr-shadow-sm">
          {isPending && (
            <div className="cr-flex cr-h-full cr-items-center cr-justify-center">
              <Spinner isFetching={true} />
            </div>
          )}

          {isError && !isPending && (
            <div className="cr-flex cr-h-full cr-items-center cr-justify-center cr-text-red-600">
              Unable to load gates for this team. Please try again later.
            </div>
          )}

          {!isError && !isPending && gateSummaries.length === 0 && (
            <div className="cr-flex cr-h-full cr-items-center cr-justify-center">
              No gates available for this team yet.
            </div>
          )}

          {!isError && !isPending && gateSummaries.length > 0 && (
            <>
              {selectedGate && gateSummaries.length > 1 && (
                <div className="cr-absolute cr-right-4 cr-top-4 cr-z-[1000]">
                  <button
                    type="button"
                    onClick={handleZoomAll}
                    className="cr-rounded-md cr-bg-white cr-px-3 cr-py-1 cr-text-xs cr-font-semibold cr-text-[#03365D] cr-shadow-sm hover:cr-bg-cirrusBlue-25"
                  >
                    All Gates
                  </button>
                </div>
              )}
              <MapContainer center={mapCenter} zoom={13} scrollWheelZoom className="cr-h-full cr-w-full">
                <FitBounds bounds={bounds} trigger={fitBoundsTrigger} />
                <BoundsListener onBoundsChange={handleMapBoundsChange} />
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {gateSummaries.map((marker) => (
                  <Fragment key={marker.id}>
                    <Marker position={marker.start} icon={createGateIcon('start', String(marker.order))}>
                      <Popup>
                        <div className="cr-min-w-[180px]">
                          <p className="cr-font-semibold">{marker.popupLabel}</p>
                          <p className="cr-text-xs cr-text-gray-500">Gate #{marker.order}</p>
                          <p className="cr-text-xs cr-text-gray-500">Start gate</p>
                          <p className="cr-text-sm cr-text-gray-600">
                            {marker.isValid ? 'Valid' : 'Not yet validated'}
                          </p>
                        </div>
                      </Popup>
                    </Marker>
                    {showBothGates && marker.end && (
                      <Marker position={marker.end} icon={createGateIcon('end', String(marker.order))}>
                        <Popup>
                          <div className="cr-min-w-[180px]">
                            <p className="cr-font-semibold">{marker.popupLabel}</p>
                            <p className="cr-text-xs cr-text-gray-500">Gate #{marker.order}</p>
                            <p className="cr-text-xs cr-text-gray-500">End gate</p>
                            <p className="cr-text-sm cr-text-gray-600">
                              {marker.isValid ? 'Valid' : 'Not yet validated'}
                            </p>
                          </div>
                        </Popup>
                      </Marker>
                    )}
                  </Fragment>
                ))}
              </MapContainer>
            </>
          )}
        </div>

        <div className="cr-rounded-xl cr-bg-white cr-p-6 cr-shadow-sm">
          <h2 className="cr-mb-2 cr-text-lg cr-font-semibold">Gate Pairs</h2>
          <p className="cr-mb-4 cr-text-sm cr-text-gray-600">
            Gate pairs are numbered for quick reference. Use Center to focus the map or Select to apply the gates.
          </p>

          <div className="cr-flex cr-flex-col cr-gap-3">
            {gateSummaries.map((marker) => {
              const isActive = marker.id === activeGateId;
              return (
                <div
                  key={marker.id}
                  onClick={() => handleFocusGate(marker.id)}
                  className={`cr-flex cr-cursor-pointer cr-items-center cr-justify-between 
                  cr-rounded-lg cr-border cr-p-4 cr-transition-colors ${
                    isActive ? 'cr-border-cirrusBlue-200 cr-bg-cirrusBlue-25' : 'cr-border-gray-200'
                  }`}
                >
                  <div className="cr-flex cr-items-start cr-gap-3">
                    <span className="cr-flex cr-h-6 cr-w-6 cr-items-center cr-justify-center cr-rounded-full cr-bg-cirrusBlue-100 cr-text-xs cr-font-semibold cr-text-white">
                      {marker.order}
                    </span>
                    <div>
                      <p className="cr-font-medium">{marker.label}</p>
                      {marker.routeDescription && (
                        <p className="cr-text-xs cr-text-gray-500">{marker.routeDescription}</p>
                      )}
                      <p className="cr-text-xs cr-text-gray-500">ID: {marker.id}</p>
                    </div>
                  </div>
                  <div className="cr-flex cr-items-center cr-gap-2">
                    <Button
                      onClick={(event) => {
                        event.stopPropagation();
                        handleFocusGate(marker.id);
                      }}
                    >
                      Center
                    </Button>
                    <Button
                      onClick={(event) => {
                        event.stopPropagation();
                        handleSelectGate(marker);
                      }}
                    >
                      Select
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function FitBounds({ bounds, trigger }: { bounds: LatLngBounds | null; trigger: number }): ReactElement | null {
  const map = useMap();

  useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [map, bounds, trigger]);

  return null;
}

function BoundsListener({ onBoundsChange }: { onBoundsChange: (shouldShow: boolean) => void }): ReactElement | null {
  const map = useMapEvents({
    zoomend: () => handleBoundsChange(map, onBoundsChange),
    moveend: () => handleBoundsChange(map, onBoundsChange),
  });

  useEffect(() => {
    handleBoundsChange(map, onBoundsChange);
  }, [map, onBoundsChange]);

  return null;
}

function handleBoundsChange(map: LeafletMap, onBoundsChange: (shouldShow: boolean) => void): void {
  const bounds = map.getBounds();
  if (!bounds.isValid()) return;
  const diagonalDistanceMeters = bounds.getNorthEast().distanceTo(bounds.getSouthWest());
  onBoundsChange(diagonalDistanceMeters <= 10_000);
}
